import React, { useState, useEffect } from 'react';
import { defaultTheme, createCustomTheme, applyThemeToCSS } from '../config/theme';

// Theme Context for global theme management
export const ThemeContext = React.createContext();

export const ThemeProvider = ({ children, customTheme = null }) => {
  const [theme, setTheme] = useState(customTheme || defaultTheme);
  
  useEffect(() => {
    applyThemeToCSS(theme);
  }, [theme]);
  
  const updateTheme = (newTheme) => {
    const updatedTheme = createCustomTheme(newTheme);
    setTheme(updatedTheme);
  };
  
  const resetTheme = () => {
    setTheme(defaultTheme);
  };
  
  return (
    <ThemeContext.Provider value={{ theme, updateTheme, resetTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook to use theme context
export const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme Customization Component (for demo purposes)
export const ThemeCustomizer = () => {
  const { theme, updateTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  
  const handleColorChange = (colorKey, value) => {
    updateTheme({
      colors: {
        [colorKey]: value
      }
    });
  };
  
  const handleFontChange = (fontKey, value) => {
    updateTheme({
      fonts: {
        [fontKey]: value
      }
    });
  };
  
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed right-4 bg-accent-yellow text-black px-4 py-2 rounded-lg shadow-lg hover:bg-yellow-400 transition-colors duration-200 z-50"
        style={{ top: '500px' }}
      >
        Customize Theme
      </button>
    );
  }
  
  return (
    <div className="fixed right-4 bg-white p-6 rounded-lg shadow-xl z-50 w-80 max-h-96 overflow-y-auto" style={{ top: '500px' }}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Theme Customizer</h3>
        <button
          onClick={() => setIsOpen(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hero Background Color
          </label>
          <input
            type="color"
            value={theme.colors.heroBg}
            onChange={(e) => handleColorChange('heroBg', e.target.value)}
            className="w-full h-10 rounded border"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Partners Background Color
          </label>
          <input
            type="color"
            value={theme.colors.partnersBg}
            onChange={(e) => handleColorChange('partnersBg', e.target.value)}
            className="w-full h-10 rounded border"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Accent Color
          </label>
          <input
            type="color"
            value={theme.colors.accentYellow}
            onChange={(e) => handleColorChange('accentYellow', e.target.value)}
            className="w-full h-10 rounded border"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Font Family
          </label>
          <select
            value={theme.fonts.primary}
            onChange={(e) => handleFontChange('primary', e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="Inter">Inter</option>
            <option value="Roboto">Roboto</option>
            <option value="Open Sans">Open Sans</option>
            <option value="Lato">Lato</option>
            <option value="Montserrat">Montserrat</option>
            <option value="Poppins">Poppins</option>
          </select>
        </div>
      </div>
    </div>
  );
};
